import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

export interface Budget {
  id: string;
  department_id: string;
  department_name?: string;
  total_budget: number;
  allocated_amount: number;
  remaining_amount: number;
  period_type: 'monthly' | 'quarterly' | 'yearly';
  period_start: string;
  period_end: string;
  is_active: boolean;
}

export interface BudgetTransaction {
  id: string;
  budget_id: string;
  requisition_id?: string;
  purchase_order_id?: string;
  transaction_type: 'requisition_approved' | 'po_created' | 'po_received' | 'po_cancelled' | 'adjustment';
  amount: number;
  description: string;
  created_by: string;
  created_at: string;
}

export interface BudgetAlert {
  id: string;
  budget_id: string;
  alert_type: 'warning' | 'critical';
  message: string;
  threshold_percentage: number;
  current_percentage: number;
  is_acknowledged: boolean;
  created_at: string;
}

export interface BudgetOverview {
  departments: Budget[];
  total_budget: number;
  total_allocated: number;
  total_remaining: number;
  alerts: BudgetAlert[];
}

export function useBudget() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const getBudgetOverview = () =>
    useQuery({
      queryKey: ['budget-overview', user?.hospital_id],
      queryFn: () => api.getBudgetOverview(),
      enabled: !!user?.hospital_id,
    });

  const getBudgets = () =>
    useQuery({
      queryKey: ['budgets', user?.hospital_id],
      queryFn: () => api.getBudgets(),
      enabled: !!user?.hospital_id,
    });

  const getBudget = (id: string) =>
    useQuery({
      queryKey: ['budget', id],
      queryFn: () => api.getBudget(id),
      enabled: !!id,
    });

  const getBudgetTransactions = (params?: { budget_id?: string; page?: number; page_size?: number }) =>
    useQuery({
      queryKey: ['budget-transactions', params],
      queryFn: () => api.getBudgetTransactions(params),
      enabled: !!user?.hospital_id,
    });

  const getBudgetAlerts = () =>
    useQuery({
      queryKey: ['budget-alerts', user?.hospital_id],
      queryFn: () => api.getBudgetAlerts(),
      enabled: !!user?.hospital_id,
    });

  const createBudget = () =>
    useMutation({
      mutationFn: (data: Partial<Budget>) => api.createBudget(data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['budgets'] });
        queryClient.invalidateQueries({ queryKey: ['budget-overview'] });
      },
      onError: (error) => {
        console.error('Failed to create budget:', error);
        throw error; // Re-throw to let caller handle
      },
    });

  const updateBudget = () =>
    useMutation({
      mutationFn: ({ id, data }: { id: string; data: Partial<Budget> }) =>
        api.updateBudget(id, data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['budgets'] });
        queryClient.invalidateQueries({ queryKey: ['budget-overview'] });
      },
      onError: (error) => {
        console.error('Failed to update budget:', error);
        throw error;
      },
    });

  const acknowledgeAlert = () =>
    useMutation({
      mutationFn: (alertId: string) => api.acknowledgeBudgetAlert(alertId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['budget-alerts'] });
        queryClient.invalidateQueries({ queryKey: ['budget-overview'] });
      },
      onError: (error) => {
        console.error('Failed to acknowledge alert:', error);
        throw error;
      },
    });

  const validateBudget = (amount: number, departmentId?: string) =>
    useQuery({
      queryKey: ['budget-validate', amount, departmentId],
      queryFn: () => api.validateBudget(amount, departmentId),
      enabled: amount > 0,
    });

  return {
    getBudgetOverview,
    getBudgets,
    getBudget,
    getBudgetTransactions,
    getBudgetAlerts,
    createBudget,
    updateBudget,
    acknowledgeAlert,
    validateBudget,
  };
}
